import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import {
  ListPageShell, ListPageHeader, ListSearchBar, ListPagination,
} from '../components/ui/ListPageChrome';

export default function ItemMaster() {
  const navigate = useNavigate();
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const [loading, setLoading] = useState(true);
  const [pageInput, setPageInput] = useState('1');
  const limit = 25;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page, limit });
        if (search) params.set('search', search);
        const data = await api.get(`/items?${params}`);
        if (!cancelled) {
          setRows(data.rows);
          setTotal(data.total);
        }
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [page, search]);

  const totalPages = Math.ceil(total / limit) || 1;

  useEffect(() => {
    setPageInput(String(page));
  }, [page]);

  const submitPageJump = useCallback(() => {
    const n = parseInt(String(pageInput).trim(), 10);
    if (!Number.isFinite(n)) {
      setPageInput(String(page));
      return;
    }
    const clamped = Math.min(Math.max(1, n), totalPages);
    setPage(clamped);
    setPageInput(String(clamped));
  }, [pageInput, page, totalPages]);

  return (
    <ListPageShell>
      <ListPageHeader
        title="Item Master Data"
        action={
          <button type="button" onClick={() => navigate('/item-master/new')} className="btn-primary">
            + Add New
          </button>
        }
      />

      <ListSearchBar
        value={search}
        onChange={setSearch}
        onChangeExtra={() => setPage(1)}
        placeholder="Search by Item No. or Description…"
      />

      <div className="data-table-wrap overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="table-head">
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Item No.</th>
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Description</th>
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Type</th>
              <th className="text-right px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Unit Price</th>
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Currency</th>
              <th className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sap-textLight">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sap-textLight">No items found.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.item_id}
                onClick={() => navigate(`/item-master/${r.item_no}`)}
                className="table-row even:bg-gray-50/50">
                <td className="px-4 py-3 font-semibold text-sap-blue">{r.item_no}</td>
                <td className="px-4 py-3">{r.description}</td>
                <td className="px-4 py-3">{r.item_type}</td>
                <td className="px-4 py-3 text-right font-medium">
                  {r.unit_price && Number(r.unit_price) > 0
                    ? Number(r.unit_price).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : <span className="text-sap-textLight">—</span>}
                </td>
                <td className="px-4 py-3 text-sap-textLight">{r.unit_price && Number(r.unit_price) > 0 ? (r.currency || 'INR') : '—'}</td>
                <td className="px-4 py-3">
                  {r.status === 'Active' ? (
                    <span className="badge-active">{r.status}</span>
                  ) : (
                    <span className="px-2.5 py-0.5 rounded-lg text-xs font-semibold bg-[#FDF0EE] text-[#C4574B]">{r.status}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-sap-textLight">
          <ListPagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
          <label htmlFor="item-master-page-jump" className="flex items-center gap-1.5">
            <span className="text-sap-text shrink-0">Go to page</span>
            <input
              id="item-master-page-jump"
              value={pageInput}
              onChange={e => setPageInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitPageJump(); }}
              className="w-12 h-7 px-2 text-xs border border-sap-border rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-sap-blue/20 focus:border-sap-blue"
            />
            <button type="button" onClick={submitPageJump} className="btn-secondary text-xs py-1 px-2">Go</button>
          </label>
        </div>
      )}
    </ListPageShell>
  );
}
